/**
 * Resolution Regarding Authorization of Contribution
 * Phase 1 (PITCH) document with variants by asset type
 * Requires Secretary Treasurer signature + Board Member signatures
 * 
 * NOTE: Board member signature blocks are added DYNAMICALLY by the component,
 * not in this template. The placeholder <!-- BOARD_MEMBER_SIGNATURES --> is replaced
 * at generation time with actual member signatures based on UNA membership.
 */

import { replaceMergeFields, getAllMergeFields } from './mergeFields';

export function generateAuthorizationResolutionHtml(
  workflowData: any,
  unaData: any
): string {
  const mergeFields = getAllMergeFields(workflowData, unaData);
  
  const template = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    @page { 
      size: letter; 
      margin: 1in; 
      @bottom-center { 
        content: "Page " counter(page) " of " counter(pages); 
        font-family: Cambria, Georgia, serif; 
        font-size: 10pt; 
        padding-bottom: 0.25in; 
      } 
    }
    body {
      font-family: 'Cambria', 'Georgia', serif;
      font-size: 12pt;
      line-height: 1.8;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 1in;
      color: #333;
    }
    h1 {
      text-align: center;
      font-size: 16pt;
      margin-bottom: 0.5in;
      font-weight: bold;
      text-transform: uppercase;
    }
    .whereas, .resolved {
      margin-bottom: 0.25in;
      text-align: justify;
    }
    .whereas::before {
      content: "WHEREAS, ";
      font-weight: bold;
    }
    .resolved::before {
      content: "RESOLVED, ";
      font-weight: bold;
    }
    .signature-section {
      margin-top: 0.75in;
      page-break-inside: avoid;
    }
    .signature-block {
      margin-top: 0.5in;
      padding: 0.3in;
      border: 1px solid #ddd;
      border-radius: 4px;
      background-color: #fafafa;
      page-break-inside: avoid;
    }
    .signature-line {
      border-bottom: 2px solid #333;
      width: 3.5in;
      min-height: 0.6in;
      margin-bottom: 0.15in;
      background-color: white;
    }
    .signature-label {
      font-size: 10pt;
      margin-top: 0.1in;
    }
    .date-line {
      border-bottom: 1px solid #666;
      width: 2in;
      display: inline-block;
      margin-left: 0.15in;
    }
    .section-title {
      font-size: 14pt;
      margin-bottom: 0.5in;
      border-bottom: 2px solid #333;
      padding-bottom: 0.15in;
    }
  </style>
</head>
<body>
  <h1>Resolution Regarding Authorization of Contribution</h1>
  
  ${getWhereasClauses(workflowData)}
  
  ${getResolvedClauses(workflowData)}
  
  <div class="signature-section">
    <!-- BOARD_MEMBER_SIGNATURES -->
    
    <div style="page-break-before: avoid; margin-top: 0.75in;">
      <h3 class="section-title">{{primary_officer_role}} Attestation</h3>
      <p style="margin-bottom: 0.3in; font-style: italic; font-size: 11pt;">
        The {{primary_officer_role}} hereby attests that this authorization was duly adopted:
      </p>
      
      <div class="signature-block">
        <div class="signature-line" data-signer-id="{{treasury_secretary_id}}" data-signature-role="primary_officer"></div>
        <div class="signature-label">{{treasury_secretary_name}}, {{primary_officer_role}}</div>
        <div style="margin-top: 0.15in;">Date: {{contribution_date}}</div>
      </div>
    </div>
  </div>
</body>
</html>
  `;
  
  return replaceMergeFields(template, mergeFields);
}

function getWhereasClauses(workflowData: any): string {
  const assetType = workflowData.asset_type;
  const baseWhereas = `
  <div class="whereas">
    {{contributor_name}} is a member of {{una_name}}, an unincorporated nonprofit association 
    formed under the laws of {{una_state}} and operating within ecclesiastical jurisdiction;
  </div>
  
  <div class="whereas">
    {{contributor_name}} desires to make a private member contribution to {{una_name}} to support 
    its sacred purposes and advance the stewardship of assets for the benefit of the membership;
  </div>
  `;
  
  let assetSpecific = '';
  
  if (assetType?.includes('Business Entity')) {
    assetSpecific = `
  <div class="whereas">
    {{contributor_name}} holds an ownership interest in {{entity_legal_name}}, a {{entity_type}} 
    formed under the laws of {{entity_state_of_formation}}, and desires to contribute this 
    interest to {{receiving_entity}} through private member contribution;
  </div>
  
  <div class="whereas">
    The contribution of this business entity interest will enable {{receiving_entity}} to steward 
    the entity for the advancement of the UNA's purposes, maintaining proper ecclesiastical 
    jurisdiction over its operations;
  </div>
    `;
  } else if (assetType?.includes('Real Estate')) {
    assetSpecific = `
  <div class="whereas">
    {{contributor_name}} holds an interest in real property commonly known as {{property_address}}, 
    {{property_city}}, {{property_state}}, and desires to contribute this interest to 
    {{receiving_entity}} through private member contribution;
  </div>
  
  <div class="whereas">
    The contribution of this real property will enable {{receiving_entity}} to steward the 
    property for purposes including: {{property_purpose}};
  </div>
    `;
  } else if (assetType?.includes('Vehicle')) {
    assetSpecific = `
  <div class="whereas">
    {{contributor_name}} holds title to a vehicle described as a {{vehicle_description}}, 
    VIN {{vehicle_vin}}, and desires to contribute this vehicle to {{receiving_entity}} 
    through private member contribution;
  </div>
  
  <div class="whereas">
    The contribution of this vehicle will enable {{receiving_entity}} to provide transportation 
    for officers and members in furtherance of the UNA's purposes;
  </div>
    `;
  } else if (assetType === 'Cash/Bank Account') {
    assetSpecific = `
  <div class="whereas">
    {{contributor_name}} desires to make a cash contribution described as: {{contribution_description}}, 
    to {{receiving_entity}} through private member contribution;
  </div>
  
  <div class="whereas">
    This cash contribution will provide financial resources for {{receiving_entity}} to advance 
    the sacred purposes of the UNA;
  </div>
    `;
  } else if (assetType === 'Intellectual Property') {
    assetSpecific = `
  <div class="whereas">
    {{contributor_name}} holds rights to intellectual property described as: {{ip_description}}, 
    and desires to contribute these rights to {{receiving_entity}} through private member contribution;
  </div>
  
  <div class="whereas">
    The contribution of this intellectual property will enable {{receiving_entity}} to steward 
    these assets for the benefit of the membership;
  </div>
    `;
  } else {
    assetSpecific = `
  <div class="whereas">
    {{contributor_name}} holds an asset described as a {{asset_type}}, and desires to contribute 
    this asset to {{receiving_entity}} through private member contribution;
  </div>
  
  <div class="whereas">
    The contribution of this asset will enable {{receiving_entity}} to steward it for the 
    advancement of the UNA's sacred purposes;
  </div>
    `;
  }
  
  return baseWhereas + assetSpecific;
}

function getResolvedClauses(workflowData: any): string {
  const assetType = workflowData.asset_type;
  
  let assetSpecific = '';
  
  if (assetType?.includes('Business Entity')) {
    assetSpecific = `
  <div class="resolved">
    {{contributor_name}} hereby authorizes the private contribution of their member interest in 
    {{entity_legal_name}} to {{receiving_entity}} through the method of {{contribution_method}};
  </div>
  
  <div class="resolved">
    {{contributor_name}} authorizes the execution of a Private Assignment of Member Interest 
    to formalize this contribution, and authorizes {{receiving_entity}} to assume stewardship 
    and management of the contributed entity;
  </div>
    `;
  } else if (assetType?.includes('Real Estate')) {
    assetSpecific = `
  <div class="resolved">
    {{contributor_name}} hereby authorizes the private contribution of their interest in the 
    real property located at {{property_address}}, {{property_city}}, {{property_state}}, to 
    {{receiving_entity}} through the method of {{contribution_method}};
  </div>
  
  <div class="resolved">
    {{contributor_name}} authorizes the execution of a {{deed_type}} to formalize this 
    contribution, and authorizes {{receiving_entity}} to assume stewardship of the property;
  </div>
    `;
  } else if (assetType?.includes('Vehicle')) {
    assetSpecific = `
  <div class="resolved">
    {{contributor_name}} hereby authorizes the private contribution of the vehicle described 
    as {{vehicle_description}}, VIN {{vehicle_vin}}, to {{receiving_entity}} through the 
    method of {{contribution_method}};
  </div>
  
  <div class="resolved">
    {{contributor_name}} authorizes the execution of title transfer documentation and all 
    instruments necessary to formalize this contribution;
  </div>
    `;
  } else {
    assetSpecific = `
  <div class="resolved">
    {{contributor_name}} hereby authorizes the private contribution of the asset described 
    as {{asset_type}} to {{receiving_entity}} through the method of {{contribution_method}};
  </div>
  
  <div class="resolved">
    {{contributor_name}} authorizes the execution of all instruments necessary to formalize 
    this contribution and transfer stewardship to {{receiving_entity}};
  </div>
    `;
  }
  
  return `
  <div class="resolved">
    This contribution is made freely and voluntarily within the ecclesiastical jurisdiction of 
    {{una_name}}, and is not subject to public law gift or donation rules;
  </div>
  
  ${assetSpecific}
  
  <div class="resolved">
    This authorization is effective as of {{contribution_date}}, and this resolution shall be 
    preserved in the ecclesiastical records of {{una_name}}.
  </div>
  `;
}
